import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import usePersonalization from 'components/Hooks/usePersonalization';
import { useSelector } from 'react-redux';
import CheckedIcon from './images/checkbox-checked.svg';
import UncheckedIcon from './images/checkbox-unchecked.svg';

const useStyles = makeStyles(theme => ({
  icon: {
    height: 22,
    width: 22,
  },
  smallIcon: {
    height: 16,
    width: 16,
  },
  label: {
    ...theme.typography.overline,
    color: theme.palette.text.primary,
  },
  smallLabel: {
    lineHeight: 'unset',
  },
  checked: {
    color: '#01619B !important',
    // fontFamily: 'MadaBold !important',
  },
  root: {
    width: '100%',
    marginLeft: 0,
    alignItems: 'flex-start',
    '& > span': {
      padding: 0,
      marginBottom: 4,
    },
    '& > span:first-child': {
      paddingRight: 8,
    },
  },
  normalLetterSpacing: {
    letterSpacing: 'normal',
  },
  manageLink: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  manageLinkBlock: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    marginTop: 8,
    marginLeft: 30,
    display: 'inline-block',
    fontSize: '14px',
    '&:hover': {
      opacity: 0.8,
    },
  },
  checkboxWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

function SubscriptionOptions({
  subscription,
  onChange,
  isModal,
  isSmall,
  useDisclaimerLabel,
  displayCustomConsent,
  showManageLink,
  onManagePreferencesClick,
}) {
  const classes = useStyles();
  const clientDetails = useSelector(makeSelectClientDetails());
  const [subscriptionOption] = useSiteCopySelector(['account']);
  const longTextHelper = _get(
    subscriptionOption,
    'pageCopy.subscriptionsLongTextHelper',
  );
  const disclaimerLabel = _get(
    subscriptionOption,
    'pageCopy.subscriptionsDisclaimerLabel',
  );
  const customConsent = _get(
    clientDetails,
    'customAuthentication.customConsent.label',
  );
  const {
    emailSubscriptionOption,
    emailPreferencesOptions,
  } = usePersonalization();

  const mainEmail = emailSubscriptionOption;

  const availableEmailOptions = React.useMemo(
    () => (emailPreferencesOptions || []).map(opt => opt.value),
    [emailPreferencesOptions],
  );

  const hasAnySubscription = subscription.some(item =>
    availableEmailOptions.includes(item),
  );

  const handleEmailSubscriptionChange = () => {
    const current = Array.isArray(subscription) ? subscription : [];
    const hasCustomConsent = current.includes('customconsent');

    let next;
    if (hasAnySubscription) {
      next = current.filter(item => !availableEmailOptions.includes(item));
    } else {
      next = [...current, ...availableEmailOptions].filter(
        item => item !== 'customconsent',
      );
      next = Array.from(new Set(next));
    }

    if (hasCustomConsent && !next.includes('customconsent')) {
      next.push('customconsent');
    }

    onChange(next);
  };

  return (
    <Grid
      container
      spacing={1}
      direction={isModal ? 'row' : 'column'}
      alignItems={isModal ? 'center' : 'flex-start'}
      justifyContent={isModal ? 'space-between' : 'flex-start'}
    >
      {displayCustomConsent && customConsent && (
        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                focusRipple
                checked={subscription.includes('customconsent')}
                onChange={() => {
                  const current = Array.isArray(subscription)
                    ? subscription
                    : [];
                  const next = current.includes('customconsent')
                    ? current.filter(v => v !== 'customconsent')
                    : current.concat('customconsent');
                  onChange(next);
                }}
                value="customconsent"
                checkedIcon={
                  <img
                    src={CheckedIcon}
                    className={_classNames({
                      [classes.icon]: !isSmall,
                      [classes.smallIcon]: isSmall,
                    })}
                    alt=""
                  />
                }
                icon={
                  <img
                    src={UncheckedIcon}
                    className={_classNames({
                      [classes.icon]: !isSmall,
                      [classes.smallIcon]: isSmall,
                    })}
                    alt=""
                  />
                }
                inputProps={{ 'aria-label': customConsent }}
              />
            }
            classes={{
              label: _classNames({
                [classes.label]: true,
                [classes.smallLabel]: isSmall,
                [classes.checked]: subscription.includes('customconsent'),
              }),
              root: classes.root,
            }}
            label={customConsent}
          />
        </Grid>
      )}
      <Grid item>
        <div className={classes.checkboxWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                focusRipple
                checked={hasAnySubscription}
                onChange={handleEmailSubscriptionChange}
                checkedIcon={
                  <img
                    src={CheckedIcon}
                    className={_classNames({
                      [classes.icon]: !isSmall,
                      [classes.smallIcon]: isSmall,
                    })}
                    alt=""
                  />
                }
                icon={
                  <img
                    src={UncheckedIcon}
                    className={_classNames({
                      [classes.icon]: !isSmall,
                      [classes.smallIcon]: isSmall,
                    })}
                    alt=""
                  />
                }
                inputProps={{
                  'aria-label': 'Updates and curated content communications',
                }}
              />
            }
            classes={{
              label: _classNames({
                [classes.label]: true,
                [classes.smallLabel]: isSmall,
              }),
              root: classes.root,
            }}
            label={
              isSmall
                ? mainEmail?.longText || mainEmail?.text || ''
                : mainEmail?.text || ''
            }
          />
          {showManageLink && (
            <span
              className={classes.manageLinkBlock}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                if (onManagePreferencesClick) {
                  onManagePreferencesClick();
                }
              }}
              role="button"
              tabIndex={0}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (onManagePreferencesClick) {
                    onManagePreferencesClick();
                  }
                }
              }}
            >
              {_get(subscriptionOption, 'pageCopy.manageEmailPreferencesLink')}
            </span>
          )}
        </div>
      </Grid>
      {isSmall && (
        <Grid item>
          <Typography
            variant="overline"
            color="textPrimary"
            className={_classNames({
              [classes.normalLetterSpacing]: useDisclaimerLabel,
            })}
          >
            {useDisclaimerLabel && <>{disclaimerLabel}&nbsp;</>}
            {longTextHelper}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}

SubscriptionOptions.propTypes = {
  subscription: PropTypes.array,
  onChange: PropTypes.func,
  isModal: PropTypes.bool,
  isSmall: PropTypes.bool,
  useDisclaimerLabel: PropTypes.bool,
  displayCustomConsent: PropTypes.bool,
  showManageLink: PropTypes.bool,
  onManagePreferencesClick: PropTypes.func,
};

SubscriptionOptions.defaultProps = {
  isModal: false,
  isSmall: false,
  showManageLink: false,
  onManagePreferencesClick: null,
};

export default SubscriptionOptions;
